import { META_INSTANT_FORM_CONTACT_FIELD_OPTIONS } from "./metaInstantForm.js";

export const INSTANT_FORM_PREVIEW_CONTACT_LIMIT = 2;

const readText = (value) => (typeof value === "string" ? value.trim() : "");

const hasText = (value) => readText(value).length > 0;

const hasItems = (value) => Array.isArray(value) && value.length > 0;

const hasInstantFormDetail = (detail) =>
  hasText(detail?.introHeadline) ||
  hasText(detail?.introDescription) ||
  hasItems(detail?.contactFields);

// Preview copy may live on the ad value, duplicateValues, or recommendations.
export const readInstantFormPreviewSource = (form) => {
  if (!form || typeof form !== "object") return null;
  const detail =
    form.duplicateValues && typeof form.duplicateValues === "object"
      ? form.duplicateValues
      : form;
  if (!hasInstantFormDetail(detail)) return null;
  return detail;
};

const readContactFieldKey = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";
  return readText(item.value || item.type);
};

export const pickMetaInstantFormPreviewStorageFields = (source) => {
  const detail = readInstantFormPreviewSource(source);
  if (!detail) return {};
  const introHeadline = readText(detail.introHeadline);
  const introDescription = readText(detail.introDescription);
  const allowedContactFields = new Set(
    META_INSTANT_FORM_CONTACT_FIELD_OPTIONS.map((option) => option.value)
  );
  const contactFields = (
    Array.isArray(detail.contactFields) ? detail.contactFields : []
  )
    .map(readContactFieldKey)
    .filter((value) => value && allowedContactFields.has(value));
  const stored = {};
  if (introHeadline) stored.introHeadline = introHeadline;
  if (introDescription) stored.introDescription = introDescription;
  if (contactFields.length) stored.contactFields = contactFields;
  return stored;
};

export const mergeMetaInstantFormPreviewStorageFields = (
  form,
  recommendation
) => ({
  ...pickMetaInstantFormPreviewStorageFields(recommendation),
  ...pickMetaInstantFormPreviewStorageFields(form),
});

export const matchesRecommendedInstantForm = (form, recommendation) => {
  if (!form?.id || !recommendation) return false;
  if (
    recommendation.id != null &&
    String(recommendation.id) === String(form.id)
  ) {
    return true;
  }
  if (recommendation.name && form.name && recommendation.name === form.name) {
    return true;
  }
  return false;
};

export const resolveInstantFormPreviewSource = (form, recommendation) => {
  const fromForm = readInstantFormPreviewSource(form);
  if (fromForm) return fromForm;
  if (!matchesRecommendedInstantForm(form, recommendation)) return null;
  return readInstantFormPreviewSource(recommendation);
};

const collectContactFields = (source) => {
  const selected = new Set(
    (Array.isArray(source?.contactFields) ? source.contactFields : [])
      .map(readContactFieldKey)
      .filter(Boolean)
  );
  return META_INSTANT_FORM_CONTACT_FIELD_OPTIONS.filter((option) =>
    selected.has(option.value)
  ).map((option) => ({ labelKey: option.label }));
};

export const buildInstantFormPreview = (
  source,
  { contactLimit = INSTANT_FORM_PREVIEW_CONTACT_LIMIT } = {}
) => {
  const contactFields = collectContactFields(source);
  const visibleLimit =
    Number.isFinite(contactLimit) && contactLimit > 0 ? contactLimit : 0;
  return {
    title: readText(source?.introHeadline),
    description: readText(source?.introDescription),
    contactFields: contactFields.slice(0, visibleLimit),
    moreCount: Math.max(0, contactFields.length - visibleLimit),
  };
};

export const hasInstantFormPreviewContent = (preview) =>
  Boolean(
    preview?.title ||
      preview?.description ||
      preview?.contactFields?.length ||
      preview?.moreCount > 0
  );
